<template>
  <div>
    <b-modal
      :active.sync="isModalActive"
      :width="760"
      scroll="keep"
      :on-cancel="hide"
    >
      <div class="assignment-traceability-modal bg-white border rounded shadow border-blue-light">
        <div class="flex items-start justify-between px-5 pt-5 pb-3 border-b border-grey-lighter">
          <div class="pr-4">
            <div class="flex items-center text-blue">
              <b-icon icon="project-diagram" pack="fas" size="is-small" class="mr-2"></b-icon>
              <span class="text-sm font-semibold uppercase">
                {{ $t('assignment_traceability_title') }}
              </span>
            </div>
            <div class="mt-1 text-xs text-grey-dark">
              {{ assignmentName }}
            </div>
          </div>
          <div class="flex items-center">
            <span
              v-if="!loading && lines.length"
              class="px-2 py-1 mr-3 text-xs font-semibold rounded-full bg-blue-lightest text-blue"
            >
              {{ lines.length }}
            </span>
            <button type="button" class="text-grey-dark hover:text-blue" @click="hide">
              <b-icon icon="times" pack="fas" size="is-small"></b-icon>
            </button>
          </div>
        </div>

        <div class="px-5 py-4 overflow-y-auto assignment-traceability-body">
          <div v-if="loading" class="flex justify-center py-10">
            <b-icon icon="spinner" pack="fas" size="is-medium" class="fa-pulse text-blue"></b-icon>
          </div>

          <div v-else-if="!lines.length" class="py-10 text-center">
            <b-icon icon="stream" pack="fas" size="is-large" class="mb-3 text-grey-light"></b-icon>
            <div class="text-sm text-grey-dark">{{ $t('no_traceability_lines') }}</div>
          </div>

          <div v-else class="traceability-timeline">
            <div
              v-for="(line, index) in lines"
              :key="'trace_line_' + line.id"
              class="traceability-item"
              :class="{ 'is-last': index === lines.length - 1 }"
            >
              <div class="traceability-rail">
                <span class="traceability-dot" :class="statusDotClass(line.status)"></span>
              </div>

              <div class="traceability-card" :class="statusCardClass(line.status)">
                <div class="flex flex-wrap items-center justify-between mb-2">
                  <span class="px-2 py-1 mr-2 text-xs font-semibold uppercase rounded" :class="statusBadgeClass(line.status)">
                    {{ statusLabel(line) }}
                  </span>
                  <span class="text-xs font-medium text-grey-dark">
                    {{ dateToText(line.created_at) }}
                  </span>
                </div>

                <div class="flex items-center mb-3">
                  <img
                    class="object-cover w-8 h-8 mr-2 rounded-full bg-grey-lighter"
                    :src="userAvatar(line)"
                    :alt="userName(line)"
                    onerror="this.src='https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'"
                  />
                  <div class="min-w-0">
                    <div class="text-xs font-semibold text-grey-darker truncate">{{ userName(line) }}</div>
                    <div v-if="userArea(line)" class="text-xs text-grey-dark truncate">{{ userArea(line) }}</div>
                  </div>
                </div>

                <div class="mb-2 text-xs text-grey-darker">
                  <div class="font-medium">{{ actionText(line) }}</div>
                  <div v-if="dateIntervalText(line)" class="mt-1 text-grey-dark">
                    {{ dateIntervalText(line) }}
                  </div>
                </div>

                <div
                  v-if="line.observation"
                  class="p-2 mb-2 text-xs bg-white rounded text-grey-darker"
                  v-html="line.observation"
                ></div>

                <div v-if="line.documents && line.documents.length" class="flex flex-row flex-wrap">
                  <b-tooltip
                    v-for="doc in line.documents"
                    :key="'doc_' + (doc.document_id || doc.id)"
                    :label="doc.name || doc.original_filename"
                    position="is-top"
                  >
                    <div
                      class="w-12 h-12 m-1 overflow-hidden rounded-lg shadow cursor-pointer bg-grey"
                      @click="showPreview(doc)"
                    >
                      <img
                        class="object-cover w-12 h-12"
                        :src="documentThumbnail(doc)"
                        onerror="this.src='https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'"
                      />
                    </div>
                  </b-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <assignment-document-preview
      ref="previewModal"
      :entity-id="entityId"
      entity-type="activity"
      :can-delete="false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import assignmentDocumentPreview from '·/components/assignment-document-preview'

export default {
  name: 'assignment-lines-traceability-modal',

  components: {
    assignmentDocumentPreview,
  },

  props: {
    entityId: { type: Number, required: true },
    canRecordActualprogress: { type: Boolean, default: false },
  },

  data() {
    return {
      isModalActive: false,
      loading: false,
      assignment: null,
      lines: [],
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    assignmentName() {
      if (!this.assignment) return ''
      return this.assignment.name || this.assignment.description || ''
    },

    unitName() {
      if (!this.assignment) return ''
      if (this.assignment.unit && this.assignment.unit.name) return this.assignment.unit.name
      return this.assignment.unit_name || ''
    },

    isTaskAssignment() {
      const type = this.assignment && this.assignment.type
      return type === 'task' || type === 'App\\Models\\Task' || (typeof type === 'string' && type.endsWith('\\Task'))
    },
  },

  methods: {
    show(assignmentRow) {
      if (!assignmentRow || !assignmentRow.id) return
      this.isModalActive = true
      this.assignment = {
        id: assignmentRow.id,
        name: assignmentRow.description || assignmentRow.name || '',
        type: assignmentRow.type,
        unit_name: assignmentRow.unit_name,
      }
      this.lines = []
      this.loadLines(assignmentRow.id)
    },

    hide() {
      this.isModalActive = false
      this.loading = false
      this.assignment = null
      this.lines = []
    },

    async loadLines(assignmentId) {
      this.loading = true
      try {
        const { data } = await axios.get('/api/v2/assignment/' + assignmentId, {
          params: { withLines: 1 },
        })
        const assignment = (data && data.data && data.data.assignment) || (data && data.assignment) || null
        if (!assignment) {
          this.lines = []
          return
        }

        this.assignment = {
          ...this.assignment,
          ...assignment,
          name: assignment.name || (this.assignment && this.assignment.name) || '',
        }

        const hideActual = this.project && this.project.can_progress == 0
        const lines = Array.isArray(assignment.lines) ? assignment.lines.slice() : []
        this.lines = lines
          .filter((line) => !(hideActual && line.status == 10))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      } catch (e) {
        this.lines = []
        this.$buefy.toast.open({
          message: this.$t('error'),
          type: 'is-danger',
          duration: 3000,
        })
      } finally {
        this.loading = false
      }
    },

    dateToText(date) {
      if (!date) return '—'
      return moment(date).format('DD/MM/YYYY HH:mm')
    },

    dateIntervalText(line) {
      if (!line || !line.start_date) return ''
      const start = moment(line.start_date).isValid()
        ? moment(line.start_date).format('DD/MM/YYYY')
        : line.start_date
      if (!line.end_date) return start
      const end = moment(line.end_date).isValid()
        ? moment(line.end_date).format('DD/MM/YYYY')
        : line.end_date
      return this.$t('resource_date_interval', [start, end])
    },

    userName(line) {
      const user = line && line.user
      if (!user) return '—'
      if (user.basic && user.basic.Nombre) return user.basic.Nombre
      if (user.Nombre) return user.Nombre
      if (user.name || user.surname) return [user.name, user.surname].filter(Boolean).join(' ')
      return '—'
    },

    userArea(line) {
      const user = line && line.user
      if (!user) return ''
      if (user.basic && user.basic.area) return user.basic.area
      if (typeof user.area === 'string') return user.area
      if (user.area && user.area.name) return user.area.name
      return ''
    },

    userAvatar(line) {
      const user = line && line.user
      if (!user) return 'https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'
      if (user.basic && user.basic.avatar) return user.basic.avatar
      if (user.avatar && String(user.avatar).indexOf('http') === 0) return user.avatar
      return 'https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'
    },

    documentThumbnail(doc) {
      if (!doc) return 'https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'
      if (doc.file && doc.file.thumbnail) return doc.file.thumbnail
      if (doc.thumbnail) return doc.thumbnail
      return 'https://epc-tracker-assets.s3.eu-west-3.amazonaws.com/public/document.jpeg'
    },

    quantitySuffix(line) {
      if (this.isTaskAssignment) return '%'
      const unit = (line && line.unit && line.unit.name) || this.unitName
      return unit ? (' ' + unit) : ''
    },

    formatQty(value) {
      if (value === null || value === undefined || value === '') return ''
      const num = Number(value)
      if (Number.isNaN(num)) return String(value)
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },

    statusLabel(line) {
      const status = line && line.status
      switch (status) {
        case 0:
          return this.canRecordActualprogress ? this.$t('inspected_progress') : this.$t('execute')
        case 1:
          return this.$t('pending_confirmation')
        case 2:
          return this.$t('execution_rejected')
        case 3:
        case 5:
          return this.$t('validated_action')
        case 4:
          return this.$t('rejected_action')
        case 6:
        case 8:
          return this.$t('quality_validated_action')
        case 7:
          return this.$t('quality_rejected_action')
        case 10:
          return this.$t('actual_progress')
        case 11:
          return this.$t('comment_single')
        case 20:
          return this.$t('issue_created')
        case 21:
        case 27:
          return this.$t('task_issue_resolved')
        case 22:
          return this.$t('task_issue_without_blocking')
        case 23:
          return this.$t('task_issue_with_task_blocking')
        case 24:
          return this.$t('task_issue_with_activity_blocking')
        case 25:
          return this.$t('task_issue_with_project_blocking')
        case 26:
          return this.$t('task_issue_lock_solved')
        case 28:
          return this.$t('task_issue_with_parent_activity_blocking')
        case 30:
          return this.$t('validation_status_pending_validation')
        case 31:
          return this.$t('rejected')
        case 32:
          return this.$t('accepted')
        default:
          return this.$t('action')
      }
    },

    actionText(line) {
      if (!line) return ''

      if (line.completed == 1 && Number(line.executed_quantity) === 0) {
        return this.$t('completed_the_resource')
      }

      if (line.status == 11) {
        return this.$t('comment_single')
      }

      if ([20, 21, 22, 23, 24, 25, 26, 27, 28, 30, 31, 32].includes(line.status)) {
        return this.statusLabel(line)
      }

      const qty = this.formatQty(line.executed_quantity)
      const suffix = this.quantitySuffix(line)
      let text = this.statusLabel(line)
      if (qty !== '') {
        text += ': ' + qty + suffix
      }

      if (
        (line.status == 5 || line.status == 8 || line.status == 0) &&
        line.executed_quantity != null &&
        line.total_quantity != null &&
        Number(line.executed_quantity) !== Number(line.total_quantity)
      ) {
        const rejected = this.formatQty(Number(line.total_quantity) - Number(line.executed_quantity))
        text += ' · ' + this.$t('rejected') + ' ' + rejected + suffix
      }

      return text
    },

    statusDotClass(status) {
      if ([2, 4, 7, 20, 23, 25, 31].includes(status)) return 'is-danger'
      if ([1, 21, 26, 30].includes(status)) return 'is-warning'
      if ([10, 3, 5, 6, 8, 27, 32].includes(status)) return 'is-success'
      if ([22, 24, 28].includes(status)) return 'is-orange'
      return 'is-info'
    },

    statusBadgeClass(status) {
      if ([2, 4, 7, 20, 23, 25, 31].includes(status)) return 'bg-red-lightest text-red'
      if ([1, 21, 26, 30].includes(status)) return 'bg-yellow-lightest text-yellow-dark'
      if ([10, 3, 5, 6, 8, 27, 32].includes(status)) return 'bg-green-lightest text-green-dark'
      if ([22, 24, 28].includes(status)) return 'bg-orange-lightest text-orange'
      return 'bg-blue-lightest text-blue'
    },

    statusCardClass(status) {
      if ([2, 4, 7, 20, 23, 25, 31].includes(status)) return 'border-red-light'
      if ([1, 21, 26, 30].includes(status)) return 'border-yellow-light'
      if ([10, 3, 5, 6, 8, 27, 32].includes(status)) return 'border-green-light'
      if ([22, 24, 28].includes(status)) return 'border-orange-light'
      return 'border-blue-lighter'
    },

    showPreview(document) {
      if (!document || !this.$refs.previewModal) return
      const normalized = Object.assign({}, document, {
        document_id: document.document_id || document.id,
        name: document.name || document.original_filename || 'document',
        user: document.user || { Nombre: '—', avatar: '', email: '' },
      })
      this.$refs.previewModal.show(normalized)
    },
  },
}
</script>

<style scoped lang="scss">
.assignment-traceability-modal {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.assignment-traceability-body {
  max-height: calc(85vh - 88px);
}

.traceability-timeline {
  position: relative;
}

.traceability-item {
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 12px;
  position: relative;
  padding-bottom: 16px;

  &:not(.is-last)::before {
    content: '';
    position: absolute;
    left: 9px;
    top: 18px;
    bottom: 0;
    width: 2px;
    background: #d9e4f0;
  }
}

.traceability-rail {
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

.traceability-dot {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #c5d4e6;
  background: #679dbf;

  &.is-success { background: #48bb78; box-shadow: 0 0 0 1px #9ae6b4; }
  &.is-warning { background: #ecc94b; box-shadow: 0 0 0 1px #f6e05e; }
  &.is-danger { background: #f56565; box-shadow: 0 0 0 1px #feb2b2; }
  &.is-orange { background: #ed8936; box-shadow: 0 0 0 1px #fbd38d; }
  &.is-info { background: #679dbf; box-shadow: 0 0 0 1px #a8c5d8; }
}

.traceability-card {
  border: 1px solid #d9e4f0;
  border-radius: 10px;
  padding: 12px 14px;
  background: #f8fbfe;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 10px rgba(96, 126, 185, 0.12);
  }
}
</style>
